import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Avatar, IconButton, TextField,
  Tooltip, Chip, Divider, CircularProgress, Menu, MenuItem,
  ListItemIcon, ListItemText, Badge, Switch,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  SendOutlined, DeleteOutlineOutlined, PushPinOutlined,
  MoreVertOutlined, PersonAddOutlined, SearchOutlined,
  CheckCircleOutlined, StarOutlined, CloseOutlined,
  ChatBubbleOutlineOutlined, AdminPanelSettingsOutlined,
  AddOutlined, EditOutlined, PersonRemoveOutlined,
  ChatOutlined, ConstructionOutlined,
} from '@mui/icons-material';
import { api } from '../../../hooks/useApi';

const CHAT_COLOR   = '#0a7aff';
const STAFF_COLOR  = '#f59e0b';
const PINNED_COLOR = '#22c55e';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatTime(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDate(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - d) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function getInitials(name = '') {
  return name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase() || '?';
}

function avatarColor(name = '') {
  const colors = ['#1075BA', '#8b4fcc', '#00a882', '#e67e22', '#e63946', '#2d9e4e'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

// ── Lobby Admin Panel ─────────────────────────────────────────────────────────
// Replaces the old simple AdminAssignPanel.
// Lets you: view assigned lobby admins, toggle their mode (full ↔ chat_only),
// remove them, assign an existing admin user, or create a brand-new lobby admin.

const MODE_LABEL = { full: 'Full Edit', chat_only: 'Chat Only' };
const MODE_COLOR = { full: '#00a882', chat_only: CHAT_COLOR };

function LobbyAdminPanel({ chatId, onClose }) {
  const [view, setView]           = useState('list');   // 'list' | 'assign' | 'create'
  const [admins, setAdmins]       = useState([]);
  const [allUsers, setAllUsers]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [search, setSearch]       = useState('');
  const [error, setError]         = useState('');

  // create-form state
  const [newFirst,    setNewFirst]    = useState('');
  const [newLast,     setNewLast]     = useState('');
  const [newEmail,    setNewEmail]    = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newMode,     setNewMode]     = useState('chat_only');

  // assign-form state
  const [assignMode, setAssignMode] = useState('chat_only');

  const loadAdmins = useCallback(() => {
    setLoading(true);
    api.get(`/chat-channels/${chatId}/lobby-admins`)
      .then(({ data: res }) => { if (res.success) setAdmins(res.data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [chatId]);

  useEffect(() => { loadAdmins(); }, [loadAdmins]);

  // Load all non-lobby-admin users for the assign panel
  useEffect(() => {
    if (view !== 'assign') return;
    api.get('/admin-users')
      .then(({ data: res }) => {
        if (res.success) {
          // Exclude users already assigned as lobby admins to this channel
          const assignedIds = admins.map(a => a._id);
          setAllUsers((res.data || []).filter(u => !u.isLobbyAdmin || !assignedIds.includes(u._id)));
        }
      })
      .catch(() => {});
  }, [view, admins]);

  const handleToggleMode = async (adminId, currentMode) => {
    const next = currentMode === 'full' ? 'chat_only' : 'full';
    try {
      await api.patch(`/chat-channels/${chatId}/lobby-admins/${adminId}`, { lobbyAdminMode: next });
      setAdmins(prev => prev.map(a => a._id === adminId ? { ...a, lobbyAdminMode: next } : a));
    } catch {}
  };

  const handleRemove = async (adminId) => {
    try {
      await api.delete(`/chat-channels/${chatId}/lobby-admins/${adminId}`);
      setAdmins(prev => prev.filter(a => a._id !== adminId));
    } catch {}
  };

  const handleAssign = async (userId) => {
    setSaving(true); setError('');
    try {
      const { data: res } = await api.post(`/chat-channels/${chatId}/lobby-admins`, {
        mode: 'assign', adminUserId: userId, lobbyAdminMode: assignMode,
      });
      if (res.success) { setAdmins(prev => [...prev, res.data]); setView('list'); }
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to assign admin');
    } finally { setSaving(false); }
  };

  const handleCreate = async () => {
    if (!newFirst || !newLast || !newEmail || !newPassword) {
      setError('All fields are required'); return;
    }
    setSaving(true); setError('');
    try {
      const { data: res } = await api.post(`/chat-channels/${chatId}/lobby-admins`, {
        mode: 'create',
        firstName: newFirst, lastName: newLast,
        email: newEmail, password: newPassword,
        lobbyAdminMode: newMode,
      });
      if (res.success) {
        setAdmins(prev => [...prev, res.data]);
        setNewFirst(''); setNewLast(''); setNewEmail(''); setNewPassword('');
        setView('list');
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to create lobby admin');
    } finally { setSaving(false); }
  };

  const inputStyle = {
    border: '1px solid #e4e9f0', borderRadius: '8px',
    px: 1.5, py: 0.9, fontSize: '0.79rem', color: '#3c4555',
    background: '#fafbfc', width: '100%', fontFamily: 'inherit',
    outline: 'none', boxSizing: 'border-box',
    '&:focus': { borderColor: CHAT_COLOR },
  };

  const filteredUsers = allUsers.filter(u =>
    `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box sx={{ position: 'absolute', inset: 0, zIndex: 20, background: '#fff', display: 'flex', flexDirection: 'column' }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: '1px solid #f0f4f8', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {view !== 'list' && (
            <IconButton size="small" onClick={() => { setView('list'); setError(''); }} sx={{ color: '#9ea8b3', mr: -0.5 }}>
              <CloseOutlined sx={{ fontSize: 14 }} />
            </IconButton>
          )}
          <AdminPanelSettingsOutlined sx={{ fontSize: 17, color: CHAT_COLOR }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1a1a2e' }}>
            {view === 'list'   ? 'Lobby Admins'       :
             view === 'assign' ? 'Assign Existing User' : 'Create Lobby Admin'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: '#9ea8b3' }}>
          <CloseOutlined sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* ── LIST VIEW ── */}
      {view === 'list' && (
        <>
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={20} sx={{ color: CHAT_COLOR }} />
              </Box>
            ) : admins.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 5, px: 3 }}>
                <AdminPanelSettingsOutlined sx={{ fontSize: 36, color: alpha(CHAT_COLOR, 0.2), mb: 1 }} />
                <Typography sx={{ fontSize: '0.78rem', color: '#9ea8b3' }}>
                  No lobby admins assigned yet.
                </Typography>
              </Box>
            ) : admins.map(admin => {
              const fullName = `${admin.firstName} ${admin.lastName}`;
              const mode = admin.lobbyAdminMode || 'chat_only';
              return (
                <Box key={admin._id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, borderBottom: '1px solid #f8f9fb' }}>
                  <Avatar sx={{ width: 32, height: 32, background: avatarColor(fullName), fontSize: '0.7rem', fontWeight: 700, flexShrink: 0 }}>
                    {admin.avatar
                      ? <img src={admin.avatar} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                      : getInitials(fullName)}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.79rem', color: '#1a1a2e', lineHeight: 1.2 }}>{fullName}</Typography>
                    <Typography sx={{ fontSize: '0.67rem', color: '#9ea8b3' }} noWrap>{admin.email}</Typography>
                  </Box>
                  {/* Mode toggle chip */}
                  <Tooltip title={`Switch to ${mode === 'full' ? 'Chat Only' : 'Full Edit'}`} placement="top" arrow>
                    <Box
                      onClick={() => handleToggleMode(admin._id, mode)}
                      sx={{
                        px: 1, py: 0.3, borderRadius: '6px', cursor: 'pointer',
                        background: alpha(MODE_COLOR[mode], 0.1),
                        color: MODE_COLOR[mode],
                        fontSize: '0.65rem', fontWeight: 700,
                        display: 'flex', alignItems: 'center', gap: 0.5,
                        transition: 'background 0.15s',
                        flexShrink: 0,
                        '&:hover': { background: alpha(MODE_COLOR[mode], 0.2) },
                      }}
                    >
                      {mode === 'full'
                        ? <ConstructionOutlined sx={{ fontSize: 11 }} />
                        : <ChatOutlined sx={{ fontSize: 11 }} />}
                      {MODE_LABEL[mode]}
                    </Box>
                  </Tooltip>
                  <Tooltip title="Remove" placement="top" arrow>
                    <IconButton size="small" onClick={() => handleRemove(admin._id)} sx={{ color: '#c0c8d4', '&:hover': { color: '#e63946' } }}>
                      <PersonRemoveOutlined sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>

          {/* Footer buttons */}
          <Box sx={{ px: 2, py: 1.25, borderTop: '1px solid #f0f4f8', display: 'flex', gap: 1, flexShrink: 0 }}>
            <Box
              onClick={() => { setView('create'); setError(''); }}
              sx={{
                flex: 1, py: 0.9, borderRadius: '8px', background: CHAT_COLOR, color: '#fff',
                fontSize: '0.76rem', fontWeight: 700, textAlign: 'center', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                '&:hover': { background: '#0060cc' }, transition: 'background 0.15s',
              }}
            >
              <AddOutlined sx={{ fontSize: 14 }} /> New User
            </Box>
            <Box
              onClick={() => { setView('assign'); setSearch(''); setError(''); }}
              sx={{
                flex: 1, py: 0.9, borderRadius: '8px',
                border: `1px solid ${CHAT_COLOR}`, color: CHAT_COLOR,
                fontSize: '0.76rem', fontWeight: 700, textAlign: 'center', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                '&:hover': { background: alpha(CHAT_COLOR, 0.06) }, transition: 'background 0.15s',
              }}
            >
              <PersonAddOutlined sx={{ fontSize: 14 }} /> Existing
            </Box>
          </Box>
        </>
      )}

      {/* ── ASSIGN EXISTING USER VIEW ── */}
      {view === 'assign' && (
        <>
          {/* Mode selector */}
          <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid #f0f4f8', flexShrink: 0 }}>
            <Typography sx={{ fontSize: '0.7rem', color: '#9ea8b3', mb: 0.75, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Access Level
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {['chat_only', 'full'].map(m => (
                <Box
                  key={m}
                  onClick={() => setAssignMode(m)}
                  sx={{
                    flex: 1, py: 0.7, borderRadius: '7px', textAlign: 'center',
                    fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer',
                    background: assignMode === m ? alpha(MODE_COLOR[m], 0.12) : '#f5f7fa',
                    color: assignMode === m ? MODE_COLOR[m] : '#9ea8b3',
                    border: `1px solid ${assignMode === m ? MODE_COLOR[m] : 'transparent'}`,
                    transition: 'all 0.15s',
                  }}
                >
                  {MODE_LABEL[m]}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Search */}
          <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #f0f4f8', flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, background: '#f5f7fa', borderRadius: '8px', px: 1.5, py: 0.75 }}>
              <SearchOutlined sx={{ fontSize: 15, color: '#9ea8b3' }} />
              <input
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search users…"
                style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: '0.8rem', color: '#3c4555', width: '100%', fontFamily: 'inherit' }}
              />
            </Box>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {filteredUsers.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography sx={{ fontSize: '0.78rem', color: '#9ea8b3' }}>No users found</Typography>
              </Box>
            ) : filteredUsers.map(u => {
              const fullName = `${u.firstName} ${u.lastName}`;
              return (
                <Box
                  key={u._id}
                  onClick={() => handleAssign(u._id)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    px: 2, py: 1.1, cursor: saving ? 'default' : 'pointer',
                    '&:hover': { background: '#f8f9fb' },
                  }}
                >
                  <Avatar sx={{ width: 30, height: 30, background: avatarColor(fullName), fontSize: '0.68rem', fontWeight: 700 }}>
                    {getInitials(fullName)}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.79rem', color: '#1a1a2e', lineHeight: 1.2 }}>{fullName}</Typography>
                    <Typography sx={{ fontSize: '0.67rem', color: '#9ea8b3' }} noWrap>{u.email}</Typography>
                  </Box>
                  <AddOutlined sx={{ fontSize: 15, color: alpha(CHAT_COLOR, 0.4) }} />
                </Box>
              );
            })}
          </Box>
          {error && <Typography sx={{ px: 2, py: 0.75, fontSize: '0.72rem', color: '#e63946', flexShrink: 0 }}>{error}</Typography>}
        </>
      )}

      {/* ── CREATE NEW USER VIEW ── */}
      {view === 'create' && (
        <>
          <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5 }}>
            {/* Mode selector */}
            <Typography sx={{ fontSize: '0.7rem', color: '#9ea8b3', mb: 0.75, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Access Level
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              {['chat_only', 'full'].map(m => (
                <Box
                  key={m}
                  onClick={() => setNewMode(m)}
                  sx={{
                    flex: 1, py: 0.7, borderRadius: '7px', textAlign: 'center',
                    fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer',
                    background: newMode === m ? alpha(MODE_COLOR[m], 0.12) : '#f5f7fa',
                    color: newMode === m ? MODE_COLOR[m] : '#9ea8b3',
                    border: `1px solid ${newMode === m ? MODE_COLOR[m] : 'transparent'}`,
                    transition: 'all 0.15s',
                  }}
                >
                  {MODE_LABEL[m]}
                </Box>
              ))}
            </Box>

            {/* Fields */}
            {[
              { label: 'First Name', value: newFirst, set: setNewFirst, type: 'text' },
              { label: 'Last Name',  value: newLast,  set: setNewLast,  type: 'text' },
              { label: 'Email',      value: newEmail,  set: setNewEmail,  type: 'email' },
              { label: 'Password',   value: newPassword, set: setNewPassword, type: 'password' },
            ].map(({ label, value, set, type }) => (
              <Box key={label} sx={{ mb: 1.25 }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#5a6474', mb: 0.5 }}>{label}</Typography>
                <Box
                  component="input"
                  type={type}
                  value={value}
                  onChange={e => set(e.target.value)}
                  placeholder={label}
                  sx={{
                    ...inputStyle,
                    display: 'block',
                    '&:focus': { outline: 'none', borderColor: CHAT_COLOR },
                  }}
                  style={{
                    border: '1px solid #e4e9f0', borderRadius: '8px',
                    padding: '7px 12px', fontSize: '0.79rem', color: '#3c4555',
                    background: '#fafbfc', width: '100%', fontFamily: 'inherit',
                    outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </Box>
            ))}

            {error && <Typography sx={{ fontSize: '0.72rem', color: '#e63946', mt: 0.5 }}>{error}</Typography>}
          </Box>

          <Box sx={{ px: 2, py: 1.25, borderTop: '1px solid #f0f4f8', flexShrink: 0 }}>
            <Box
              onClick={saving ? undefined : handleCreate}
              sx={{
                py: 0.9, borderRadius: '8px', background: CHAT_COLOR, color: '#fff',
                fontSize: '0.78rem', fontWeight: 700, textAlign: 'center',
                cursor: saving ? 'default' : 'pointer',
                opacity: saving ? 0.7 : 1, transition: 'opacity 0.15s',
              }}
            >
              {saving ? 'Creating…' : 'Create Lobby Admin'}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

// ── Message bubble ────────────────────────────────────────────────────────────

function MessageBubble({ msg, onDelete, onPin }) {
  const [menuAnchor, setMenuAnchor] = useState(null);

  if (msg.isDeleted) return null;

  return (
    <Box
      sx={{
        display: 'flex', gap: 1, px: 2, py: 0.5,
        '&:hover': { background: '#f8f9fb' },
        '&:hover .msg-actions': { opacity: 1 },
        position: 'relative',
        ...(msg.isPinned ? { background: alpha(PINNED_COLOR, 0.04) } : {}),
      }}
    >
      {/* Avatar */}
      <Avatar sx={{
        width: 28, height: 28, flexShrink: 0, mt: '2px',
        background: msg.isStaff ? STAFF_COLOR : avatarColor(msg.authorName),
        fontSize: '0.65rem', fontWeight: 700,
      }}>
        {getInitials(msg.authorName)}
      </Avatar>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.2 }}>
          <Typography sx={{
            fontSize: '0.75rem', fontWeight: 700,
            color: msg.isStaff ? STAFF_COLOR : '#1a1a2e',
          }}>
            {msg.authorName}
          </Typography>
          {msg.isStaff && (
            <Box sx={{
              background: STAFF_COLOR, color: '#fff',
              fontSize: '0.55rem', fontWeight: 700,
              px: 0.6, py: 0.15, borderRadius: '3px', letterSpacing: '0.03em',
            }}>
              STAFF
            </Box>
          )}
          {msg.isPinned && (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.3,
              color: PINNED_COLOR, fontSize: '0.62rem', fontWeight: 600,
            }}>
              <PushPinOutlined sx={{ fontSize: 11 }} /> Pinned
            </Box>
          )}
          <Typography sx={{ fontSize: '0.65rem', color: '#c4cdd8', ml: 'auto', flexShrink: 0 }}>
            {formatTime(msg.createdAt)}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '0.8rem', color: '#3c4555', lineHeight: 1.55, wordBreak: 'break-word' }}>
          {msg.message}
        </Typography>
      </Box>

      {/* Hover actions */}
      <Box
        className="msg-actions"
        sx={{
          position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
          display: 'flex', gap: 0.25, opacity: 0, transition: 'opacity 0.12s',
          background: '#fff', borderRadius: '6px', border: '1px solid #e8edf2',
          boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
        }}
      >
        <Tooltip title={msg.isPinned ? 'Unpin' : 'Pin'} placement="top" arrow>
          <IconButton size="small" onClick={() => onPin(msg._id, !msg.isPinned)}
            sx={{ p: '4px', color: msg.isPinned ? PINNED_COLOR : '#9ea8b3', '&:hover': { color: PINNED_COLOR } }}>
            <PushPinOutlined sx={{ fontSize: 13 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete" placement="top" arrow>
          <IconButton size="small" onClick={() => onDelete(msg._id)}
            sx={{ p: '4px', color: '#9ea8b3', '&:hover': { color: '#ef4444' } }}>
            <DeleteOutlineOutlined sx={{ fontSize: 13 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

// ── Conversation list item ────────────────────────────────────────────────────

function ConversationItem({ chat, isActive, onClick }) {
  const lastMsg = chat._lastMessage;
  const unread  = chat._unreadCount || 0;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex', gap: 1.25, px: 1.75, py: 1.25, cursor: 'pointer',
        background: isActive ? alpha(CHAT_COLOR, 0.07) : 'transparent',
        borderLeft: `3px solid ${isActive ? CHAT_COLOR : 'transparent'}`,
        transition: 'all 0.12s',
        '&:hover': {
          background: isActive ? alpha(CHAT_COLOR, 0.07) : '#f8f9fb',
        },
      }}
    >
      {/* Icon */}
      <Box sx={{
        width: 38, height: 38, borderRadius: '10px', flexShrink: 0,
        background: isActive ? alpha(CHAT_COLOR, 0.12) : '#f0f4f8',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: isActive ? CHAT_COLOR : '#9ea8b3',
      }}>
        <ChatBubbleOutlineOutlined sx={{ fontSize: 17 }} />
      </Box>

      {/* Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.2 }}>
          <Typography sx={{
            fontWeight: isActive ? 700 : 600, fontSize: '0.79rem',
            color: isActive ? CHAT_COLOR : '#1a1a2e', lineHeight: 1.2,
          }} noWrap>
            {chat.name || 'Chat'}
          </Typography>
          {lastMsg && (
            <Typography sx={{ fontSize: '0.63rem', color: '#c4cdd8', flexShrink: 0, ml: 0.5 }}>
              {formatDate(lastMsg.createdAt)}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: '0.71rem', color: '#9ea8b3', lineHeight: 1.3 }} noWrap>
            {lastMsg ? `${lastMsg.authorName}: ${lastMsg.message}` : 'No messages yet'}
          </Typography>
          {unread > 0 && (
            <Box sx={{
              ml: 0.5, minWidth: 16, height: 16, borderRadius: '8px',
              background: CHAT_COLOR, color: '#fff',
              fontSize: '0.6rem', fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              px: 0.5, flexShrink: 0,
            }}>
              {unread > 99 ? '99+' : unread}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ── Main ChatTab ──────────────────────────────────────────────────────────────


/**
 * ChatTab
 *
 * Props:
 *   chatHotspots      — hotspots with hotspotType === 'chat' from the active lobby
 *   activeChatHotspot — hotspot to pre-select when modal opens (optional)
 *   event             — event object
 *
 * Group mode:  standard channel view — one conversation for all.
 * Direct mode: left column shows participant threads; right column shows selected thread.
 */
export function ChatTab({ chatHotspots = [], activeChatHotspot, event }) {
  const allChats = chatHotspots.map(h => ({
    _id:       h.id,
    name:      h.label || h.hotspotAction?.chatTitle || 'Chat',
    channelId: h.hotspotAction?.channelId || null,
    mode:      h.hotspotAction?.mode || 'group',
    _hotspot:  h,
  }));

  const [activeChatId,   setActiveChatId]   = useState(activeChatHotspot?.id || allChats[0]?._id || null);
  const [messages,       setMessages]       = useState([]);
  const [threads,        setThreads]        = useState([]); // direct mode: list of participant threads
  const [activeThreadId, setActiveThreadId] = useState(null); // direct mode: selected participantId
  const [loading,        setLoading]        = useState(false);
  const [composing,      setComposing]      = useState('');
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  const [sendingAs,      setSendingAs]      = useState({ name: 'Event Staff', isStaff: true });
  const bottomRef = useRef(null);
  const pollRef   = useRef(null);

  const activeChat = allChats.find(c => c._id === activeChatId) || null;
  const channelId  = activeChat?.channelId || null;
  const mode       = activeChat?.mode || 'group';

  // ── Load threads (direct mode) ────────────────────────────────────────────
  const loadThreads = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const { data: res } = await api.get(`/chat-channels/${cid}/threads`);
      if (res.success) setThreads(res.data || []);
    } catch (err) {
      console.error('[ChatTab] load threads error:', err);
    }
  }, []);

  // ── Load messages ─────────────────────────────────────────────────────────
  const loadMessages = useCallback(async (cid, threadId) => {
    if (!cid) return;
    try {
      const params = threadId ? `?limit=100&participantId=${encodeURIComponent(threadId)}` : '?limit=100';
      const { data: res } = await api.get(`/chat-channels/${cid}/messages${params}`);
      if (res.success) setMessages(res.data || []);
    } catch (err) {
      console.error('[ChatTab] load messages error:', err);
    }
  }, []);

  // Re-init when channel changes
  useEffect(() => {
    setMessages([]);
    setThreads([]);
    setActiveThreadId(null);
    clearInterval(pollRef.current);
    if (!channelId) return;

    setLoading(true);
    if (mode === 'direct') {
      loadThreads(channelId).finally(() => setLoading(false));
      pollRef.current = setInterval(() => loadThreads(channelId), 5000);
    } else {
      loadMessages(channelId, null).finally(() => setLoading(false));
      pollRef.current = setInterval(() => loadMessages(channelId, null), 5000);
    }
    return () => clearInterval(pollRef.current);
  }, [channelId, mode, loadMessages, loadThreads]);

  // Re-load messages when active thread changes (direct mode)
  useEffect(() => {
    if (mode !== 'direct' || !channelId) return;
    clearInterval(pollRef.current);
    setMessages([]);
    if (!activeThreadId) return;
    setLoading(true);
    loadMessages(channelId, activeThreadId).finally(() => setLoading(false));
    pollRef.current = setInterval(() => loadMessages(channelId, activeThreadId), 5000);
    return () => clearInterval(pollRef.current);
  }, [activeThreadId, channelId, mode, loadMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // ── Send ──────────────────────────────────────────────────────────────────
  const handleSend = useCallback(async () => {
    const text = composing.trim();
    if (!text || !channelId) return;
    // Direct mode: must have a thread selected to reply into
    if (mode === 'direct' && !activeThreadId) return;
    setComposing('');
    try {
      const { data: res } = await api.post(`/chat-channels/${channelId}/messages`, {
        message:    text,
        authorName: sendingAs.name,
        authorType: 'admin',
        isStaff:    sendingAs.isStaff,
        // Direct mode: reply is scoped to the selected participant's thread
        ...(mode === 'direct' ? { participantId: activeThreadId } : {}),
      });
      if (res.success) {
        setMessages(prev => [...prev, res.data]);
        // Refresh thread list so last message updates
        if (mode === 'direct') loadThreads(channelId);
      }
    } catch (err) {
      console.error('[ChatTab] send error:', err);
    }
  }, [composing, channelId, mode, activeThreadId, sendingAs, loadThreads]);

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = useCallback(async (messageId) => {
    if (!channelId) return;
    setMessages(prev => prev.map(m => m._id === messageId ? { ...m, isDeleted: true } : m));
    try {
      await api.delete(`/chat-channels/${channelId}/messages/${messageId}`);
    } catch (err) {
      console.error('[ChatTab] delete error:', err);
    }
  }, [channelId]);

  // ── Pin ───────────────────────────────────────────────────────────────────
  const handlePin = useCallback(async (messageId, pin) => {
    setMessages(prev => prev.map(m => m._id === messageId ? { ...m, isPinned: pin } : m));
    try {
      await api.patch(`/chat-channels/${channelId}/messages/${messageId}/pin`, { isPinned: pin });
    } catch (err) {
      console.error('[ChatTab] pin error:', err);
    }
  }, [channelId]);

  // ── Group messages by date ────────────────────────────────────────────────
  const grouped = messages.reduce((acc, msg) => {
    if (msg.isDeleted) return acc;
    const key = new Date(msg.createdAt).toDateString();
    if (!acc[key]) acc[key] = [];
    acc[key].push(msg);
    return acc;
  }, {});

  const pinnedMessages = messages.filter(m => m.isPinned && !m.isDeleted);

  // ── LEFT COLUMN content depends on mode ───────────────────────────────────
  // Group: list of chat channels (one per hotspot)
  // Direct: list of participant threads within the selected channel
  const showThreadList = mode === 'direct' && !!channelId;

  return (
    <Box sx={{ display: 'flex', height: '100%', background: '#fff' }}>

      {/* ── LEFT COLUMN (1/3) ─────────────────────────────────────────────── */}
      <Box sx={{
        width: '33.33%', borderRight: '1px solid #f0f4f8', flexShrink: 0,
        display: 'flex', flexDirection: 'column', background: '#fafbfc',
      }}>
        {/* Header */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f0f4f8', flexShrink: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1a1a2e' }}>
            {showThreadList ? 'Conversations' : 'Chats'}
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', color: '#9ea8b3', mt: 0.2 }}>
            {showThreadList
              ? `${threads.length} participant${threads.length !== 1 ? 's' : ''}`
              : `${allChats.length} channel${allChats.length !== 1 ? 's' : ''}`}
          </Typography>
        </Box>

        {/* Search bar */}
        <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid #f0f4f8', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, background: '#f0f4f8', borderRadius: '8px', px: 1.25, py: 0.7 }}>
            <SearchOutlined sx={{ fontSize: 14, color: '#b0bac8' }} />
            <Typography sx={{ fontSize: '0.75rem', color: '#b0bac8' }}>Search…</Typography>
          </Box>
        </Box>

        {/* List */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {showThreadList ? (
            // ── Direct mode: participant thread list ──────────────────────
            threads.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <ChatBubbleOutlineOutlined sx={{ fontSize: 28, color: '#d4dbe6', mb: 1 }} />
                <Typography sx={{ fontSize: '0.75rem', color: '#b0bac8' }}>
                  No conversations yet.
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#c4cdd8', mt: 0.5, lineHeight: 1.5 }}>
                  Participants who send a message will appear here.
                </Typography>
              </Box>
            ) : threads.map(thread => {
              const isActive = activeThreadId === thread._id;
              return (
                <Box
                  key={thread._id}
                  onClick={() => setActiveThreadId(thread._id)}
                  sx={{
                    display: 'flex', gap: 1.25, px: 1.75, py: 1.25, cursor: 'pointer',
                    background: isActive ? alpha(CHAT_COLOR, 0.07) : 'transparent',
                    borderLeft: `3px solid ${isActive ? CHAT_COLOR : 'transparent'}`,
                    transition: 'all 0.12s',
                    '&:hover': { background: isActive ? alpha(CHAT_COLOR, 0.07) : '#f8f9fb' },
                  }}
                >
                  {/* Avatar */}
                  <Box sx={{
                    width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                    background: isActive ? alpha(CHAT_COLOR, 0.15) : '#e8edf2',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.72rem', fontWeight: 700,
                    color: isActive ? CHAT_COLOR : '#9ea8b3',
                  }}>
                    {getInitials(thread.participantName || 'Guest')}
                  </Box>
                  {/* Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.2 }}>
                      <Typography sx={{ fontWeight: isActive ? 700 : 600, fontSize: '0.79rem', color: isActive ? CHAT_COLOR : '#1a1a2e' }} noWrap>
                        {thread.participantName || 'Guest'}
                      </Typography>
                      {thread.lastMessageAt && (
                        <Typography sx={{ fontSize: '0.63rem', color: '#c4cdd8', flexShrink: 0, ml: 0.5 }}>
                          {formatDate(thread.lastMessageAt)}
                        </Typography>
                      )}
                    </Box>
                    <Typography sx={{ fontSize: '0.71rem', color: '#9ea8b3', lineHeight: 1.3 }} noWrap>
                      {thread.lastMessage || 'No messages'}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          ) : (
            // ── Group mode: channel list ──────────────────────────────────
            allChats.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <ChatBubbleOutlineOutlined sx={{ fontSize: 32, color: '#d4dbe6', mb: 1 }} />
                <Typography sx={{ fontSize: '0.75rem', color: '#b0bac8' }}>
                  No chat elements yet.
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#c4cdd8', mt: 0.5 }}>
                  Draw a "Chat" shape on the canvas to create one.
                </Typography>
              </Box>
            ) : allChats.map(chat => (
              <ConversationItem
                key={chat._id}
                chat={chat}
                isActive={chat._id === activeChatId}
                onClick={() => { setActiveChatId(chat._id); setActiveThreadId(null); }}
              />
            ))
          )}
        </Box>

        {/* Back button for direct mode thread view */}
        {showThreadList && (
          <Box
            onClick={() => { setActiveChatId(allChats[0]?._id || null); }}
            sx={{
              px: 2, py: 1, borderTop: '1px solid #f0f4f8', flexShrink: 0,
              display: 'flex', alignItems: 'center', gap: 0.75,
              fontSize: '0.73rem', fontWeight: 600, color: '#9ea8b3',
              cursor: 'pointer', background: '#fafbfc',
              '&:hover': { color: CHAT_COLOR },
            }}
          >
            ← Back to channels
          </Box>
        )}
      </Box>

      {/* ── RIGHT COLUMN (2/3) ────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>

        {/* Lobby admin panel overlay */}
        {adminPanelOpen && activeChat && channelId && (
          <LobbyAdminPanel
            chatId={channelId}
            onClose={() => setAdminPanelOpen(false)}
          />
        )}

        {activeChat ? (
          <>
            {/* Chat header */}
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              px: 2.5, py: 1.5, borderBottom: '1px solid #f0f4f8', flexShrink: 0, background: '#fff',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box sx={{ width: 34, height: 34, borderRadius: '9px', background: alpha(CHAT_COLOR, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', color: CHAT_COLOR }}>
                  <ChatBubbleOutlineOutlined sx={{ fontSize: 16 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.84rem', color: '#1a1a2e', lineHeight: 1.2 }}>
                    {mode === 'direct' && activeThreadId
                      ? (threads.find(t => t._id === activeThreadId)?.participantName || 'Guest')
                      : activeChat.name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.67rem', color: '#9ea8b3' }}>
                    {mode === 'direct'
                      ? (activeThreadId ? 'Direct conversation' : 'Select a participant from the left')
                      : (channelId ? 'Group channel · Channel ready' : 'Save the lobby to activate')}
                  </Typography>
                </Box>
                {/* Mode badge */}
                <Box sx={{
                  px: 0.9, py: 0.25, borderRadius: '5px', fontSize: '0.62rem', fontWeight: 700,
                  background: mode === 'direct' ? alpha('#8b4fcc', 0.1) : alpha(CHAT_COLOR, 0.1),
                  color:      mode === 'direct' ? '#8b4fcc' : CHAT_COLOR,
                }}>
                  {mode === 'direct' ? '1-on-1' : 'Group'}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {channelId && (
                  <Tooltip title="Assign admin users to this chat" placement="bottom" arrow>
                    <IconButton
                      size="small"
                      onClick={() => setAdminPanelOpen(true)}
                      sx={{ color: '#9ea8b3', border: '1px solid #e8edf2', borderRadius: '8px', px: 1.25, py: 0.5, fontSize: '0.72rem', fontWeight: 600, gap: 0.5, '&:hover': { color: CHAT_COLOR, borderColor: alpha(CHAT_COLOR, 0.3), background: alpha(CHAT_COLOR, 0.04) } }}
                    >
                      <PersonAddOutlined sx={{ fontSize: 14 }} />
                      <Box component="span" sx={{ fontSize: '0.72rem', fontWeight: 600 }}>Assign Admins</Box>
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>

            {/* Pinned messages banner */}
            {pinnedMessages.length > 0 && (
              <Box sx={{ px: 2.5, py: 0.75, background: alpha(PINNED_COLOR, 0.06), borderBottom: `1px solid ${alpha(PINNED_COLOR, 0.15)}`, display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                <PushPinOutlined sx={{ fontSize: 12, color: PINNED_COLOR }} />
                <Typography sx={{ fontSize: '0.71rem', color: PINNED_COLOR, fontWeight: 600 }} noWrap>
                  {pinnedMessages[pinnedMessages.length - 1]?.message}
                </Typography>
                <Typography sx={{ fontSize: '0.67rem', color: '#9ea8b3', ml: 'auto', flexShrink: 0 }}>
                  {pinnedMessages.length} pinned
                </Typography>
              </Box>
            )}

            {/* Messages area */}
            <Box sx={{ flex: 1, overflowY: 'auto', py: 1.5 }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                  <CircularProgress size={24} sx={{ color: CHAT_COLOR }} />
                </Box>
              ) : !channelId ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1.5, p: 4, textAlign: 'center' }}>
                  <Box sx={{ width: 52, height: 52, borderRadius: '14px', background: alpha(CHAT_COLOR, 0.08), display: 'flex', alignItems: 'center', justifyContent: 'center', color: CHAT_COLOR }}>
                    <ChatBubbleOutlineOutlined sx={{ fontSize: 26 }} />
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.84rem', color: '#1a1a2e' }}>Save the lobby first</Typography>
                  <Typography sx={{ fontSize: '0.74rem', color: '#9ea8b3', lineHeight: 1.6, maxWidth: 260 }}>
                    This chat channel will be created automatically when you save the lobby.
                  </Typography>
                </Box>
              ) : mode === 'direct' && !activeThreadId ? (
                // Direct mode — no thread selected yet
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1.5, p: 4, textAlign: 'center' }}>
                  <Box sx={{ width: 52, height: 52, borderRadius: '14px', background: alpha('#8b4fcc', 0.08), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8b4fcc' }}>
                    <ChatBubbleOutlineOutlined sx={{ fontSize: 26 }} />
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.84rem', color: '#1a1a2e' }}>Select a conversation</Typography>
                  <Typography sx={{ fontSize: '0.74rem', color: '#9ea8b3', lineHeight: 1.6, maxWidth: 260 }}>
                    {threads.length === 0
                      ? 'Waiting for participants to start a conversation.'
                      : 'Choose a participant from the left to view their messages.'}
                  </Typography>
                </Box>
              ) : Object.keys(grouped).length === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1.5, p: 4, textAlign: 'center' }}>
                  <Box sx={{ width: 52, height: 52, borderRadius: '14px', background: alpha(CHAT_COLOR, 0.08), display: 'flex', alignItems: 'center', justifyContent: 'center', color: CHAT_COLOR }}>
                    <ChatBubbleOutlineOutlined sx={{ fontSize: 26 }} />
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.84rem', color: '#1a1a2e' }}>No messages yet</Typography>
                  <Typography sx={{ fontSize: '0.74rem', color: '#9ea8b3', lineHeight: 1.6 }}>
                    Be the first to send a message in this chat.
                  </Typography>
                </Box>
              ) : (
                Object.entries(grouped).map(([dateKey, msgs]) => (
                  <Box key={dateKey}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1 }}>
                      <Box sx={{ flex: 1, height: '1px', background: '#f0f4f8' }} />
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#c4cdd8', letterSpacing: '0.05em' }}>
                        {formatDate(msgs[0].createdAt)}
                      </Typography>
                      <Box sx={{ flex: 1, height: '1px', background: '#f0f4f8' }} />
                    </Box>
                    {msgs.map(msg => (
                      <MessageBubble key={msg._id} msg={msg} onDelete={handleDelete} onPin={handlePin} />
                    ))}
                  </Box>
                ))
              )}
              <div ref={bottomRef} />
            </Box>

            {/* Send-as strip */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.75, borderTop: '1px solid #f5f7fa', background: '#fafbfc', flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.67rem', color: '#9ea8b3' }}>Sending as:</Typography>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, background: sendingAs.isStaff ? alpha(STAFF_COLOR, 0.1) : alpha(CHAT_COLOR, 0.08), color: sendingAs.isStaff ? STAFF_COLOR : CHAT_COLOR, px: 1, py: 0.35, borderRadius: '6px', fontSize: '0.71rem', fontWeight: 700, cursor: 'pointer' }}
                onClick={() => setSendingAs(prev => ({ ...prev, isStaff: !prev.isStaff }))}
              >
                <StarOutlined sx={{ fontSize: 11 }} />
                {sendingAs.isStaff ? 'Event Staff' : sendingAs.name}
              </Box>
              <Typography sx={{ fontSize: '0.63rem', color: '#c4cdd8' }}>· Click to toggle</Typography>
            </Box>

            {/* Compose area */}
            {channelId && (mode === 'group' || (mode === 'direct' && activeThreadId)) && (
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, px: 2, py: 1.5, borderTop: '1px solid #f0f4f8', background: '#fff', flexShrink: 0 }}>
                <Box sx={{ flex: 1, background: '#f5f7fa', borderRadius: '12px', px: 1.5, py: 1, display: 'flex', alignItems: 'flex-end' }}>
                  <textarea
                    value={composing}
                    onChange={e => setComposing(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                    placeholder={mode === 'direct' ? `Reply to ${threads.find(t => t._id === activeThreadId)?.participantName || 'participant'}…` : 'Type a message… (Enter to send)'}
                    rows={1}
                    style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: '0.82rem', fontFamily: 'inherit', color: '#1a1a2e', resize: 'none', lineHeight: 1.5, maxHeight: 100, overflowY: 'auto', width: '100%' }}
                  />
                </Box>
                <Box
                  onClick={handleSend}
                  sx={{ width: 38, height: 38, borderRadius: '10px', background: composing.trim() ? CHAT_COLOR : '#e8edf2', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: composing.trim() ? 'pointer' : 'default', transition: 'background 0.15s', flexShrink: 0, '&:hover': composing.trim() ? { background: '#0a6de6' } : {} }}
                >
                  <SendOutlined sx={{ fontSize: 16, color: composing.trim() ? '#fff' : '#b0bac8' }} />
                </Box>
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, p: 4, textAlign: 'center' }}>
            <Box sx={{ width: 60, height: 60, borderRadius: '16px', background: alpha(CHAT_COLOR, 0.08), display: 'flex', alignItems: 'center', justifyContent: 'center', color: CHAT_COLOR }}>
              <ChatBubbleOutlineOutlined sx={{ fontSize: 28 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#1a1a2e' }}>Select a chat</Typography>
            <Typography sx={{ fontSize: '0.74rem', color: '#9ea8b3', lineHeight: 1.6, maxWidth: 220 }}>
              Choose a chat from the left to view and moderate messages.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}